import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Children } from "@app/core/type/Children"
import { appConstants } from "@app/core/appConstants"
import { useHttp } from "@app/core/hooks/use-http"

export const DomainNameValidatorContext = React.createContext<DomainNameValidatorStore>({} as DomainNameValidatorStore)

export function DomainNameValidatorContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const [forbiddenDomainNames, setForbiddenDomainNames] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const { get } = useHttp()

  useEffect(() => {
    setLoading(true)
    get(appConstants.apiEndpoints.FORBIDDEN_DOMAIN_NAMES)
      .then((response) => response.json())
      .then((domainNameList) => {
        setForbiddenDomainNames(domainNameList)
      })
      .finally(() => setLoading(false))
  }, [get])

  const extractDomainName = useCallback((email: string) => {
    if (email && email.length > 1) {
      return email.substring(email.indexOf("@") + 1, email.length)
    } else {
      return ""
    }
  }, [])

  const isDomainNameInvalid: (email: string) => boolean = useCallback(
    (email) => {
      const domainName: string = extractDomainName(email)
      if (loading) {
        throw new Error("DOMAIN_LIST_IS_LOADING")
      }
      return forbiddenDomainNames.includes(domainName)
    },
    [extractDomainName, forbiddenDomainNames, loading]
  )

  const domainNameValidatorStore: DomainNameValidatorStore = useMemo(
    () => ({
      isDomainNameInvalid,
    }),
    [isDomainNameInvalid]
  )

  return (
    <DomainNameValidatorContext.Provider value={domainNameValidatorStore}>{children}</DomainNameValidatorContext.Provider>
  )
}

export interface DomainNameValidatorStore {
  isDomainNameInvalid(email: string): boolean
}
